import { useCallback, useEffect, useRef, useState } from "react";
import { useAppStore } from "../../store";
import { useDuelsStore } from "./store";
import type { Article } from "../wikidaily/types";
import i18n from "../../i18n/config";
import { api } from "../../api";

type FrontendAction = { action: string; payload?: unknown };

const ARTICLE_HTML_CACHE_LIMIT = 100;
const articleHtmlCache = new Map<number, string>();
const articleHtmlRequests = new Map<number, Promise<string>>();

function getCachedArticleHtml(articleId: number): string | null {
  if (!articleHtmlCache.has(articleId)) return null;

  const html = articleHtmlCache.get(articleId)!;
  articleHtmlCache.delete(articleId);
  articleHtmlCache.set(articleId, html);
  return html;
}

function setCachedArticleHtml(articleId: number, html: string) {
  if (articleHtmlCache.has(articleId)) {
    articleHtmlCache.delete(articleId);
  } else if (articleHtmlCache.size >= ARTICLE_HTML_CACHE_LIMIT) {
    const oldestArticleId = articleHtmlCache.keys().next().value;
    if (oldestArticleId !== undefined) articleHtmlCache.delete(oldestArticleId);
  }

  articleHtmlCache.set(articleId, html);
}

async function fetchArticleHtml(articleId: number): Promise<string> {
  const cachedHtml = getCachedArticleHtml(articleId);
  if (cachedHtml !== null) return cachedHtml;

  const existingRequest = articleHtmlRequests.get(articleId);
  if (existingRequest) return existingRequest;

  const request = fetch(`/wiki/en/${articleId}`, {
    credentials: "include",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`article fetch failed: ${response.status}`);
      }

      return response.text();
    })
    .then((html) => {
      setCachedArticleHtml(articleId, html);
      return html;
    })
    .finally(() => {
      articleHtmlRequests.delete(articleId);
    });

  articleHtmlRequests.set(articleId, request);
  return request;
}

function toZigMessage(message: FrontendAction): unknown {
  if (message.action === "change_accept") {
    return { to: "game", op: "change_accept", data: Boolean(message.payload) };
  }

  if (message.action === "move") {
    const payload = message.payload as
      { id?: number; article?: number } | undefined;
    return {
      to: "game",
      op: "move",
      data: { article: payload?.id ?? payload?.article },
    };
  }

  if (message.action === "reset_try") {
    return { to: "game", op: "reset_try", data: null };
  }

  if (
    message.action === "request_finish" ||
    message.action === "ready_to_finish"
  ) {
    return { to: "game", op: "request_finish", data: Boolean(message.payload) };
  }

  if (message.action === "player_info") {
    const payload = message.payload as { id?: number } | undefined;
    return { to: "server", op: "player_info", data: { id: payload?.id } };
  }

  return message;
}

function getArticleFromLoadPayload(payload: unknown): Article | null {
  if (typeof payload !== "object" || payload === null) return null;

  const id =
    (payload as { id?: unknown }).id ??
    (payload as { article?: unknown }).article;
  if (typeof id !== "number") return null;

  const title =
    (payload as { title?: unknown }).title ??
    (payload as { name?: unknown }).name;

  return {
    id,
    title: typeof title === "string" ? title : "",
    description: null,
  };
}

function getDefaultDuelsWsUrl() {
  if (import.meta.env.VITE_DUELS_WS_URL)
    return import.meta.env.VITE_DUELS_WS_URL;

  if (typeof window === "undefined") return "/ws/duels";

  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/duels`;
}

export function useDuelsSocket(url = getDefaultDuelsWsUrl()) {
  const playerId = useAppStore((state) => state.id);
  const name = useAppStore((state) => state.name);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      useDuelsStore.getState().setConnectionError(null);
      ws.send(
        JSON.stringify({
          to: "server",
          op: "auth",
          data: {
            id: playerId,
            name: name || "player",
          },
        }),
      );
    };

    ws.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (typeof data?.action !== "string") return;

        const store = useDuelsStore.getState();

        if (data.action === "article_loaded") {
          const article = (data.payload as { article?: Article } | undefined)
            ?.article;
          if (!article) return;
          const html = await fetchArticleHtml(article.id);
          store.article_loaded({ article, html });
          return;
        }

        const method = store[data.action as keyof typeof store];
        if (typeof method === "function") {
          (method as (payload: unknown) => void)(data.payload);
        }
      } catch (error) {
        console.error("failed to handle duels message", error);
        useDuelsStore
          .getState()
          .setConnectionError(
            error instanceof Error
              ? error.message
              : i18n.t("errors.messageHandlingFailed"),
          );
      }
    };

    ws.onerror = () => {
      useDuelsStore
        .getState()
        .setConnectionError(i18n.t("errors.connectionFailed"));
    };

    ws.onclose = () => {
      setConnected(false);
      if (ws === wsRef.current) {
        reconnectRef.current = setTimeout(() => connectRef.current(), 1000);
      }
    };
  }, [name, playerId, url]);

  useEffect(() => {
    connectRef.current = connect;
    api.ensureAuthenticated().then(() => connectRef.current());

    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  const send = useCallback((message: FrontendAction) => {
    if (
      message.action === "preload_move" ||
      message.action === "load_article"
    ) {
      const article = getArticleFromLoadPayload(message.payload);
      if (!article) {
        useDuelsStore
          .getState()
          .setConnectionError(i18n.t("errors.missingArticleId"));
        return false;
      }

      void fetchArticleHtml(article.id)
        .then((html) => {
          useDuelsStore.getState().article_loaded({ article, html });
          useDuelsStore.getState().setConnectionError(null);
        })
        .catch((error) => {
          console.error("failed to fetch article", error);
          useDuelsStore
            .getState()
            .setConnectionError(
              error instanceof Error
                ? error.message
                : i18n.t("errors.articleFetchFailed"),
            );
        });

      return true;
    }

    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      useDuelsStore
        .getState()
        .setConnectionError(i18n.t("errors.websocketClosedShort"));
      return false;
    }

    wsRef.current.send(JSON.stringify(toZigMessage(message)));
    return true;
  }, []);

  return { connected, reconnect: connect, send };
}
